import { useEffect, useState } from "react";
import { X, ThumbsUp, ThumbsDown, MapPin } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { timeAgo } from "@/lib/time-ago";

export type FeedMediaItem = {
  id: number;
  venueId: number;
  venueName: string;
  userId: number;
  username: string;
  mediaType: "photo" | "video";
  cloudinaryUrl: string;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  thumbsUp: number;
  thumbsDown: number;
  createdAt: string;
};

interface Props {
  media: FeedMediaItem | null;
  onClose: () => void;
  onViewVenue: (venueId: number) => void;
  onUserClick?: (userId: number) => void;
}

export function FullscreenVibeViewer({ media, onClose, onViewVenue, onUserClick }: Props) {
  const { user } = useAuth();
  const [reaction, setReaction] = useState<"thumbs_up" | "thumbs_down" | null>(null);
  const [counts, setCounts] = useState<{ thumbsUp: number; thumbsDown: number }>({ thumbsUp: 0, thumbsDown: 0 });

  useEffect(() => {
    if (media) {
      setReaction(null);
      setCounts({ thumbsUp: media.thumbsUp ?? 0, thumbsDown: media.thumbsDown ?? 0 });
    }
  }, [media?.id]);

  if (!media) return null;

  async function handleReact(next: "thumbs_up" | "thumbs_down") {
    if (!user || !media) return;
    const prev = reaction;
    const prevCounts = counts;
    const updated = { ...counts };
    let newReaction: "thumbs_up" | "thumbs_down" | null = next;
    if (prev === next) {
      newReaction = null;
      updated[next === "thumbs_up" ? "thumbsUp" : "thumbsDown"] = Math.max(0, updated[next === "thumbs_up" ? "thumbsUp" : "thumbsDown"] - 1);
    } else {
      if (prev) updated[prev === "thumbs_up" ? "thumbsUp" : "thumbsDown"] = Math.max(0, updated[prev === "thumbs_up" ? "thumbsUp" : "thumbsDown"] - 1);
      updated[next === "thumbs_up" ? "thumbsUp" : "thumbsDown"] += 1;
    }
    setReaction(newReaction);
    setCounts(updated);
    try {
      await fetch(`/api/media/${media.id}/react`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reaction: next }),
      });
    } catch {
      setReaction(prev);
      setCounts(prevCounts);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[3000] flex flex-col bg-black"
      data-testid="feed-media-viewer"
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 pt-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))] pb-3 bg-gradient-to-b from-black/90 to-transparent absolute top-0 left-0 right-0 z-20">
        <button
          data-testid="button-close-media-viewer"
          className="w-10 h-10 rounded-full bg-black/50 border border-white/10 flex items-center justify-center text-white"
          onClick={onClose}
        >
          <X className="w-5 h-5" />
        </button>
        <div className="text-right">
          <p
            data-testid={`link-viewer-user-${media.userId}`}
            role="link"
            tabIndex={0}
            onClick={() => onUserClick?.(media.userId)}
            onKeyDown={(e) => { if (e.key === "Enter") onUserClick?.(media.userId); }}
            className="text-white text-sm font-medium hover:underline cursor-pointer"
          >@{media.username || "unknown"}</p>
          <p className="text-zinc-400 text-xs">{timeAgo(media.createdAt)}</p>
        </div>
      </div>

      {/* Media */}
      <div className="flex-1 flex items-center justify-center overflow-hidden pointer-events-none">
        {!media.cloudinaryUrl ? (
          <div className="text-zinc-500 text-sm" data-testid="text-media-missing">No media available</div>
        ) : media.mediaType === "video" ? (
          <video
            src={media.cloudinaryUrl}
            poster={media.thumbnailUrl ?? undefined}
            className="max-h-full max-w-full object-contain"
            autoPlay
            loop
            playsInline
            muted
            preload="metadata"
            onError={(e) => { console.warn("[vibe viewer] video failed:", media.cloudinaryUrl); (e.currentTarget as HTMLVideoElement).style.display = "none"; }}
          />
        ) : (
          <img
            src={media.cloudinaryUrl}
            alt=""
            className="max-h-full max-w-full object-contain"
            onError={(e) => { console.warn("[vibe viewer] image failed:", media.cloudinaryUrl); (e.currentTarget as HTMLImageElement).style.display = "none"; }}
          />
        )}
      </div>

      {/* Bottom bar */}
      <div className="px-5 pb-10 pt-4 bg-gradient-to-t from-black/90 to-transparent absolute bottom-0 left-0 right-0 z-20">
        <p className="text-white font-medium mb-1">{media.venueName || "Unknown venue"}</p>
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            <button
              data-testid="button-viewer-thumbs-up"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm transition-colors ${
                user
                  ? reaction === "thumbs_up"
                    ? "bg-green-500/20 border-green-500/50 text-green-400"
                    : "bg-white/10 border-white/10 text-zinc-300 hover:bg-white/20"
                  : "opacity-40 cursor-default bg-white/10 border-white/10 text-zinc-400"
              }`}
              onClick={() => user && handleReact("thumbs_up")}
            >
              <ThumbsUp className="w-4 h-4" />
              <span>{counts.thumbsUp}</span>
            </button>
            <button
              data-testid="button-viewer-thumbs-down"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm transition-colors ${
                user
                  ? reaction === "thumbs_down"
                    ? "bg-red-500/20 border-red-500/50 text-red-400"
                    : "bg-white/10 border-white/10 text-zinc-300 hover:bg-white/20"
                  : "opacity-40 cursor-default bg-white/10 border-white/10 text-zinc-400"
              }`}
              onClick={() => user && handleReact("thumbs_down")}
            >
              <ThumbsDown className="w-4 h-4" />
              <span>{counts.thumbsDown}</span>
            </button>
          </div>
          <button
            data-testid="button-view-venue-from-media"
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold"
            onClick={() => onViewVenue(media.venueId)}
          >
            <MapPin className="w-4 h-4" />
            View Venue
          </button>
        </div>
      </div>
    </div>
  );
}
